import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';

const AboutPage = () => {
  const navigate = useNavigate();
  const [teamMembers, setTeamMembers] = useState([]);
  const [pageContent, setPageContent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAboutPageData();
  }, []);

  const fetchAboutPageData = async () => {
    try {
      setLoading(true);
      const [teamData, contentData] = await Promise.all([
        supabase?.from('team_members')?.select('*')?.eq('is_active', true)?.order('display_order', { ascending: true }),
        supabase?.from('page_content')?.select('*')?.eq('page_name', 'about')?.eq('is_active', true)?.order('display_order', { ascending: true })
      ]);

      if (teamData?.data) setTeamMembers(teamData?.data);
      if (contentData?.data) setPageContent(contentData?.data);
    } catch (err) {
      console.error('Error fetching about page data:', err);
    } finally {
      setLoading(false);
    }
  };

  const heroSection = pageContent?.find(c => c?.section_type === 'hero');
  const textBlocks = pageContent?.filter(c => c?.section_type === 'text_block');

  const values = [
    {
      icon: 'Shield',
      title: 'Security First',
      description: 'Enterprise-grade encryption and data protection for sensitive case materials'
    },
    {
      icon: 'Users',
      title: 'Expert Network',
      description: 'Connecting clients with certified fire investigation professionals'
    },
    {
      icon: 'Zap',
      title: 'Innovation',
      description: 'Leveraging AI to make preliminary analysis faster and more accessible'
    },
    {
      icon: 'Award',
      title: 'Quality',
      description: 'Rigorous validation and continuous improvement of our analysis tools'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-card border-b border-border py-4">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center">
            <img src="/assets/images/logo.png" alt="FireCaseIQ" className="h-10 object-contain" />
          </button>
          <button
            onClick={() => navigate('/')}
            className="text-sm text-muted-foreground hover:text-primary transition-smooth">
            Back to Home
          </button>
        </div>
      </header>
      {loading && (
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      )}
      {!loading && (
        <>
          <section className="bg-gradient-to-br from-primary/5 to-secondary/5 py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                {heroSection?.section_title || 'Transforming Fire Investigation with AI'}
              </h1>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                {heroSection?.section_content || 'FireCaseIQ combines cutting-edge artificial intelligence with expert fire forensics knowledge to provide rapid preliminary analysis and connect clients with certified professionals.'}
              </p>
            </div>
          </section>

          {textBlocks?.length > 0 && (
            <section className="py-16 md:py-24">
              <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
                {textBlocks?.map((block, index) => (
                  <div key={block?.id} className={`mb-12 ${index !== textBlocks?.length - 1 ? 'pb-12 border-b border-border' : ''}`}>
                    <h2 className="text-3xl font-bold text-foreground mb-4">{block?.section_title}</h2>
                    <p className="text-lg text-muted-foreground leading-relaxed">{block?.section_content}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {teamMembers?.length > 0 && (
            <section className="py-16 md:py-24 bg-muted/30">
              <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
                <h2 className="text-3xl font-bold text-foreground text-center mb-12">Meet Our Team</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {teamMembers?.map((member) => (
                    <div key={member?.id} className="bg-card rounded-xl p-6 text-center border border-border hover:shadow-lg transition-smooth">
                      <div className="w-32 h-32 rounded-full mx-auto mb-4 bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                        <Icon name="User" size={48} className="text-muted-foreground" />
                      </div>
                      <h3 className="text-xl font-bold text-foreground mb-2">Founder Information</h3>
                      <p className="text-muted-foreground text-sm">More details coming soon</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {teamMembers?.length === 0 && (
            <section className="py-16 md:py-24 bg-muted/30">
              <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
                <h2 className="text-3xl font-bold text-foreground text-center mb-12">Meet Our Team</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {[1, 2, 3]?.map((index) => (
                    <div key={index} className="bg-card rounded-xl p-6 text-center border border-border hover:shadow-lg transition-smooth">
                      <div className="w-32 h-32 rounded-full mx-auto mb-4 bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                        <Icon name="User" size={48} className="text-muted-foreground" />
                      </div>
                      <h3 className="text-xl font-bold text-foreground mb-2">Founder Information</h3>
                      <p className="text-muted-foreground text-sm">More details coming soon</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          <section className="py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
              <h2 className="text-3xl font-bold text-foreground text-center mb-12">Our Values</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {values?.map((value, index) => (
                  <div key={index} className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Icon name={value?.icon} size={32} color="#FFFFFF" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-2">{value?.title}</h3>
                    <p className="text-muted-foreground">{value?.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default AboutPage;